import { actions } from 'core/redux/features/products';
import { selectActiveProductType } from 'core/redux/features/products/reducers/productTypes';
import { makeSelectProductBySlug } from 'core/redux/features/products/reducers/products';
import { IActiveProduct } from 'core/redux/features/products/types';
import { actions as navActions } from 'core/redux/features/shared/nav';
import { selectViewTypeFilterOption } from 'core/redux/features/shared/reducers';
import { AppState, selectDeviceType } from 'core/redux/reducers';
import { voidCallback } from 'core/utils';
import { locationParser } from 'core/utils/location';
import { listen, ownerDocument, querySelectorAll } from 'dom-helpers';
import qsa from 'dom-helpers/querySelectorAll';
import React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import scrollToEl from '../../plugins/scrollToEl';
import { getViewModeFromHash, selectViewModes } from './ViewModes';

export function useScrollToProductListView() {
  const deviceType = useSelector(selectDeviceType);

  return useCallback(() => {
    if (deviceType === 'mobile') {
      return;
    }

    const findEl = qsa(document, '#product-preview-display');
    if (findEl.length > 0) {
      scrollToEl(findEl[0], document.body, 10, { block: 'end' });
    }
  }, [deviceType]);
}

export function useSelectViewModes() {
  let { hash } = useLocation();
  if (!hash) {
    hash = '#desc';
  }

  return useSelector((state: AppState) => ({
    viewMode: getViewModeFromHash(hash),
    viewModes: selectViewModes(state),
    searchSlug: hash.replace('#', ''),
  }));
}

/**
 * Returns product by given slug
 * @param props
 */
export function useSelectProductBySlug(props?: { slug: string }) {
  let { slug: paramSlug } = useParams<{ slug: string }>();

  if (!paramSlug && props?.slug) {
    paramSlug = props.slug;
  }

  const selectProduct = useMemo(makeSelectProductBySlug, []);
  return useSelector((state: AppState) => selectProduct(state, paramSlug));
}

/**
 * View product
 * It will dispatch action to fetch product info and menu courses
 * @param newProductId
 * @param slug
 */
export function useViewProductEffect({
  id: newProductId,
  slug,
}: IActiveProduct) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (newProductId && slug) {
      dispatch(
        actions.viewInfo({
          id: newProductId,
          slug,
        }),
      );
    }

    return () => {
      dispatch(actions.toggleViewMoreInfo(false));
    };
  }, [dispatch, newProductId, slug]);

  return {
    id: newProductId,
    slug,
  };
}

/**
 * Hide/Show main menu
 */
export function useHideNavMenu({
  // if true the menu will hide and false if not
  // will trigger on mount
  onMount = false,
  // if true the menu will hide and false if not
  // will trigger on unmount
  onUnMount = false,
}) {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(navActions.hideMenu('main', onMount));
    return () => {
      dispatch(navActions.hideMenu('main', onUnMount));
    };
  }, [dispatch, onMount, onUnMount]);
}

export function useHandleBackButton(viewModeId) {
  const history = useHistory();
  const location = useLocation();

  const { activeViewType, viewTypes } = useSelector((state: AppState) => ({
    activeViewType: state.viewTypes.activeViewType,
    viewTypes: selectViewTypeFilterOption(state),
  }));
  return (e: React.SyntheticEvent = null) => {
    if (e) {
      e.preventDefault();
    }

    if (viewModeId === 'gallery') {
      const loc = locationParser(location);
      history.push(loc.setHash('').toString());
      return;
    }

    const find = viewTypes.find(item => item.id === activeViewType);
    if (find && find.route) {
      history.push(find.route);
      // history.goBack()
    }
  };
}

/**
 * Asc geo position from browser
 */
export function useGeoPosition(): string[] | null {
  const [userPos, setUserPos] = useState<string[] | null>(null);

  useEffect(() => {
    const geo = navigator.geolocation;
    if (!geo) {
      return;
    }

    geo.getCurrentPosition(({ coords }) => {
      setUserPos([coords.latitude.toString(), coords.longitude.toString()]);
    });
  }, []);

  return userPos;
}

/**
 * Auto scroll
 * @param when
 * @param $el
 * @param top
 * @param $parent
 * @param delay
 */
export function useScrollToEl(
  when: Array<string | number | boolean> = [],
  {
    $el,
    top,
    $parent,
    delay,
  }: {
    $el: HTMLElement;
    top: number;
    $parent?: any;
    delay?: number;
  },
) {
  const deviceType = useSelector((state: AppState) => state.deviceType);
  const lastDeps = useRef([]);
  const isScrolling = useRef(null);
  useEffect(() => {
    if (!shallowEqual(when, lastDeps.current) && deviceType !== 'mobile') {
      lastDeps.current = when;
      clearTimeout(isScrolling.current);
      isScrolling.current = setTimeout(() => {
        scrollToEl($el, $parent ?? document.body, top);
      }, delay ?? 500);
    }
  }, [$el, $parent, delay, top, when, deviceType]);
}

/**
 * Store previous value
 * @param val
 */
export function usePrevious(val: any) {
  const refPrev = useRef(val);
  useEffect(() => {
    refPrev.current = val;
  }, [val]);
  return refPrev.current;
}

/**
 * Auto reload page to list view in 15 minutes
 * it will trigger only when the user stops interacting with the app the page
 */
export function useAutoReloadProductInMobileDevice() {
  const deviceType = useSelector((state: AppState) => state.deviceType);
  const productType = useSelector(selectActiveProductType);
  const refRemoveInterval = useRef(null);

  const createAutoReload = useCallback(() => {
    const lastUserActivity = new Date();

    const diffInMinutesNow = () => {
      const currentUserActivity = new Date();
      const diff = currentUserActivity.getTime() - lastUserActivity.getTime();
      return Math.round(diff / 60000);
    };

    const removeTimeout = setInterval(() => {
      if (diffInMinutesNow() >= 15) {
        clearInterval(removeTimeout);
        refRemoveInterval.current = null;
        window.location.href = productType.route;
      }
    }, 1000);

    return {
      stop: () => clearInterval(removeTimeout),
      counter: () => diffInMinutesNow(),
    };
  }, [productType.route]);

  useEffect(() => {
    const lastCall = refRemoveInterval.current;
    if (deviceType !== 'mobile') {
      if (lastCall) {
        lastCall.stop();
      }
      return;
    }

    if (lastCall && lastCall.counter() < 14) {
      lastCall.stop();
      refRemoveInterval.current = createAutoReload();
    } else if (!lastCall) {
      refRemoveInterval.current = createAutoReload();
    }

    return () => {
      if (lastCall) {
        lastCall.stop();
      }
    };
  }, [createAutoReload, deviceType]);

  // when user touch we will reset the timer
  const refRemoveTouchEventListener = useRef(voidCallback);
  useEffect(() => {
    if (refRemoveTouchEventListener.current) {
      refRemoveTouchEventListener.current();
    }

    if (deviceType !== 'mobile') {
      return;
    }

    const lastCall = refRemoveInterval.current;
    const elBody = querySelectorAll(ownerDocument(), 'body')[0];
    refRemoveTouchEventListener.current = listen(elBody, 'touchstart', () => {
      if (lastCall && lastCall.counter() < 14) {
        lastCall.stop();
        refRemoveInterval.current = createAutoReload();
      } else if (!lastCall) {
        refRemoveInterval.current = createAutoReload();
      }
    });

    return () => refRemoveTouchEventListener.current();
  }, [createAutoReload, deviceType]);
}
